import React from "react";
import { Badge, Box, Card, Group, Stack, Text } from "@mantine/core";
import { useTranslation } from "react-i18next";
import { CPTConfigMode, CPTResult } from "types/tests";
import { transformCPTResult } from "@utils/TestUtils";
import { Printable, usePrintableContext } from "@components/Printable";
import CPTGazeHeatmapSection from "./sections/CPTGazeHeatmapSection";
import { useCPTAssessment } from "./hooks/useCPTAssessment";
import { buildStageBuckets } from "./utils/cptStageMetrics";
import {
  ContinuousPerformanceDashboard,
  ContinuousPerformanceScoreSummary,
} from "./sections/ContinuousPerformanceDashboard";
import CPTStageBreakdownSection, {
  CPTStageSeverityMatrixSection,
} from "./sections/CPTStageBreakdownSection";
import CPTTrialDynamicsSection from "./sections/CPTTrialDynamicsSection";
import CPTPerStageGazeSection from "./sections/CPTPerStageGazeSection";
import CPTEyeTrackingSection from "./sections/CPTEyeTrackingSection";
import CPTTrialScatterSection from "./sections/CPTTrialScatterSection";
import "./sections/ContinuousPerformanceDashboard.scss";

const CPT_CONFIG_MODE_LABEL_KEYS: Record<CPTConfigMode, string> = {
  [CPTConfigMode.Default]: "TestController.cptConfigModeDefault",
  [CPTConfigMode.Demo]: "TestController.cptConfigModeDemo",
  [CPTConfigMode.Adult]: "TestController.cptConfigModeAdult",
  [CPTConfigMode.AdultDemo]: "TestController.cptConfigModeAdultDemo",
};

interface ContinuousPerformanceTestEntryProps {
  entry: CPTResult | undefined;
  header?: React.ReactNode;
}

function ContinuousPerformanceTestEntry({ entry, header }: ContinuousPerformanceTestEntryProps) {
  const transformed = transformCPTResult(entry) as CPTResult | undefined;
  const cptResult = transformed?.results ?? null;
  const assessment = useCPTAssessment(transformed);
  const { prettier } = usePrintableContext();
  const { t } = useTranslation();

  const disclaimer = (
    <Text size="xs" c="dimmed" ta="center" px="md" py="sm">
      {t("extraTests.continuousPerformance.result.disclaimer")}
    </Text>
  );

  if (!cptResult || !assessment) {
    return (
      <Printable.Section>
        <Stack>
          {header}
          <Box mx={prettier ? 0 : "xl"}>
            <Card withBorder radius="md" p="xl">
              <Text c="dimmed">{t("extraTests.continuousPerformance.result.noMetrics")}</Text>
            </Card>
          </Box>
        </Stack>
      </Printable.Section>
    );
  }

  const configModeLabelKey = cptResult.cptConfigMode
    ? CPT_CONFIG_MODE_LABEL_KEYS[cptResult.cptConfigMode]
    : null;
  const hasStageBuckets = buildStageBuckets(cptResult.stageEventList).length > 0;

  return (
    <Printable.Section>
      <Stack gap="xl" className="cpt-result-page">
        {header}
        <Box mx={prettier ? 0 : "xl"}>
          <Group gap="xs">
            <Badge variant="light">{t(cptResult.isForAdult ? "adult" : "child")}</Badge>
            {configModeLabelKey && (
              <Badge variant="light">
                {t("TestController.cptConfigModeBadge", { mode: t(configModeLabelKey) })}
              </Badge>
            )}
          </Group>
        </Box>
        <Box mx={prettier ? 0 : "xl"} className="cpt-avoid-break">
          <ContinuousPerformanceDashboard assessment={assessment} />
        </Box>
        <Box mx={prettier ? 0 : "xl"} className="cpt-avoid-break">
          <CPTGazeHeatmapSection
            isForAdult={cptResult.isForAdult ?? false}
            gazePercentageData={cptResult.gazePercentageData}
            stimuli={cptResult.stimuli}
            isForResultsPage
          />
        </Box>
        <Box mx={prettier ? 0 : "xl"} className="cpt-avoid-break">
          <CPTStageSeverityMatrixSection
            stageEvents={cptResult.stageEventList}
            stimuli={cptResult.stimuli}
            responses={cptResult.responses}
            headVelocityData={cptResult.headVelocityData}
            backendStages={cptResult.metrics?.stages}
            sectionNumber={1}
          />
        </Box>
        <Box mx={prettier ? 0 : "xl"} className="cpt-avoid-break">
          <ContinuousPerformanceScoreSummary assessment={assessment} />
        </Box>
        <Box mx={prettier ? 0 : "xl"} className="cpt-avoid-break">
          <CPTStageBreakdownSection
            stageEvents={cptResult.stageEventList}
            stimuli={cptResult.stimuli}
            responses={cptResult.responses}
            headVelocityData={cptResult.headVelocityData}
            backendStages={cptResult.metrics?.stages}
            sectionNumber={2}
          />
        </Box>
        <Box mx={prettier ? 0 : "xl"} className="cpt-avoid-break">
          <CPTTrialDynamicsSection
            stageEvents={cptResult.stageEventList}
            stimuli={cptResult.stimuli}
            responses={cptResult.responses}
            headVelocityData={cptResult.headVelocityData}
            pupilSizeData={cptResult.pupilSizeData}
            sectionNumber={3}
          />
        </Box>
        {hasStageBuckets && (
          <Box mx={prettier ? 0 : "xl"} className="cpt-avoid-break">
            <CPTPerStageGazeSection
              stageEvents={cptResult.stageEventList}
              gazePercentageData={cptResult.gazePercentageData}
              sectionNumber={4}
            />
          </Box>
        )}
        <Box mx={prettier ? 0 : "xl"} className="cpt-avoid-break">
          <CPTEyeTrackingSection
            stageEvents={cptResult.stageEventList}
            headVelocityData={cptResult.headVelocityData}
            eyeDisplacements={cptResult.eyeDisplacements}
            pupilSizeData={cptResult.pupilSizeData}
            sectionNumber={5}
          />
        </Box>
        <Box mx={prettier ? 0 : "xl"} className="cpt-avoid-break">
          <Stack gap="md">
            <CPTTrialScatterSection
              stageEvents={cptResult.stageEventList}
              stimuli={cptResult.stimuli}
              responses={cptResult.responses}
              sectionNumber={6}
            />
            {disclaimer}
          </Stack>
        </Box>
      </Stack>
    </Printable.Section>
  );
}

ContinuousPerformanceTestEntry.selfSections = true;

export default ContinuousPerformanceTestEntry;
